import { useState, useCallback, useMemo } from 'react'
import { X } from 'lucide-react'
import { QueBoard } from './QueBoard'
import { QueBoardNavigator } from './QueBoardNavigator'
import { resolveTutorialModules } from './tutorial'
import { STAGE_WIDTH } from './constants'
import type {
  QueBoardConfig,
  QueBoardResult,
  CharacterId,
  TutorialModuleId,
} from './types'
import type { QuetureTestQueBoardConfig } from '../../QuetureTestPage/types'

export interface QueBoardSessionProps {
  isOpen: boolean
  configs: QuetureTestQueBoardConfig[]
  onSessionComplete?: (results: QueBoardResult[]) => void
  /**
   * Rendering variant. Defaults to `'overlay'` which gates rendering on `isOpen`.
   * `'inline'` ignores `isOpen` and always renders so the session can live inside
   * a parent layout (e.g. LC learner ComponentRunner).
   */
  variant?: 'overlay' | 'inline'
}

export function QueBoardSession({
  isOpen,
  configs,
  onSessionComplete,
  variant = 'overlay',
}: QueBoardSessionProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [results, setResults] = useState<QueBoardResult[]>(() =>
    configs.map(() => ({ completed: false }))
  )
  const [completionInfo, setCompletionInfo] = useState<{
    problem: [number, number]
    answer: number
  } | null>(null)
  // retryKey를 변경하면 같은 currentIndex라도 QueBoard가 리마운트됨
  const [retryKey, setRetryKey] = useState(0)

  const handleComplete = useCallback(
    (result: { problem: [number, number]; answer: number }) => {
      setResults((prev) => {
        const next = [...prev]
        next[currentIndex] = {
          completed: true,
          answer: result.answer,
          problem: { left: result.problem[0], right: result.problem[1] },
        }
        return next
      })

      setCompletionInfo(result)
    },
    [currentIndex]
  )

  // 닫기: 팝업만 닫고 completed 상태 유지
  const handleClose = useCallback(() => {
    setCompletionInfo(null)
  }, [])

  // 다시풀기: 현재 문제 리셋
  const handleRetry = useCallback(() => {
    setCompletionInfo(null)
    setResults((prev) => {
      const next = [...prev]
      next[currentIndex] = { completed: false }
      return next
    })
    setRetryKey((k) => k + 1)
  }, [currentIndex])

  // 다음 문제로 이동
  const handleCompletionNext = useCallback(() => {
    setCompletionInfo(null)

    // 모두 완료 체크 (현재 결과 반영)
    const updatedResults = results.map((r, i) =>
      i === currentIndex
        ? { ...r, completed: true, answer: completionInfo?.answer, problem: completionInfo ? { left: completionInfo.problem[0], right: completionInfo.problem[1] } : r.problem }
        : r
    )
    const allDone = updatedResults.every((r) => r.completed)
    if (allDone) {
      onSessionComplete?.(updatedResults as QueBoardResult[])
      return
    }

    // 다음 미완료 문제로 이동
    const nextIncomplete = results.findIndex(
      (r, i) => i > currentIndex && !r.completed
    )
    if (nextIncomplete !== -1) {
      setCurrentIndex(nextIncomplete)
    } else {
      const firstIncomplete = results.findIndex(
        (r, i) => i !== currentIndex && !r.completed
      )
      if (firstIncomplete !== -1) {
        setCurrentIndex(firstIncomplete)
      }
    }
  }, [currentIndex, results, completionInfo, onSessionComplete])

  const handlePrev = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1))
  }, [])

  const handleNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(configs.length - 1, i + 1))
  }, [configs.length])

  // config를 안정화 (참조 변경 방지)
  const cfg = configs[currentIndex]
  const qbConfig = useMemo<QueBoardConfig>(() => ({
    problem: { left: cfg?.problem.left ?? 3, right: cfg?.problem.right ?? 5 },
    allowCustomInput: false,
    features: { ...(cfg?.features ?? { tie: true, teleport5: true, teleport10: true, bank: true, indicator: true, customInput: false }) },
    tutorial: {
      enabled: cfg?.tutorial.enabled ?? false,
      modules: (cfg?.tutorial.modules ?? []) as TutorialModuleId[],
      autoStart: cfg?.tutorial.autoStart ?? false,
    },
    character: (cfg?.character as CharacterId) ?? undefined,
  }), [currentIndex, cfg?.problem.left, cfg?.problem.right])

  const tutorialModulesResolved = useMemo(() =>
    cfg?.tutorial.enabled
      ? resolveTutorialModules((cfg.tutorial.modules ?? []) as TutorialModuleId[])
      : undefined,
    [currentIndex, cfg?.tutorial.enabled]
  )

  // resetKey: currentIndex 변경 또는 다시풀기 시 증가
  const boardResetKey = currentIndex * 1000 + retryKey

  if (configs.length === 0) return null
  if (variant === 'overlay' && !isOpen) return null

  // 모두 완료 여부
  const allCompleted = completionInfo
    ? results.every((r, i) => i === currentIndex || r.completed)
    : false

  return (
    <div style={{ width: '100%', maxWidth: STAGE_WIDTH, position: 'relative' }}>
      <QueBoard
        isOpen
        onClose={() => {}}
        config={qbConfig}
        resetKey={boardResetKey}
        tutorialModules={tutorialModulesResolved}
        onComplete={handleComplete}
      />

      {/* 정답 완료 오버레이 */}
      {completionInfo && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        >
          <div className="relative bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-3 min-w-[300px] font-cookierun-bold">
            {/* 닫기 아이콘 (우측 상단) */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-3xl">🎉</div>
            <p className="text-base font-bold text-gray-900">정답!</p>
            <p className="text-3xl text-indigo-600 mt-1 font-cookierun-black">
              {completionInfo.problem[0]} × {completionInfo.problem[1]} = {(() => {
                const tens = Math.floor(completionInfo.answer / 10)
                const ones = completionInfo.answer % 10
                if (tens === 0) return String(ones)
                if (ones === 0) return `${tens}십`
                return `${tens}십${ones}`
              })()}
            </p>
            <div className="flex gap-3 mt-4 w-full">
              <button
                onClick={handleRetry}
                className="flex-1 px-4 py-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
              >
                다시풀기
              </button>
              <button
                onClick={handleCompletionNext}
                className="flex-1 px-4 py-3 text-sm text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1"
              >
                {allCompleted ? '완료' : <>다음 문제 <span>→</span></>}
              </button>
            </div>
          </div>
        </div>
      )}

      <QueBoardNavigator
        total={configs.length}
        currentIndex={currentIndex}
        results={results}
        onSelect={setCurrentIndex}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </div>
  )
}
